"use client";

import React from "react";
import { Badge } from "@/components/ui/badge";
import { Popover, ScrollArea, Group, Stack, Text } from "@mantine/core";
import { InlineTagsEditor } from "../InlineTagsEditor";
import type { BaseEntity, SimpleTableConfig } from "../../../UniversalCRMTable/types";
import type { DetailHeaderEntityData } from "../../utils/build-detail-header-entity-data";

const READONLY_VISIBLE_TAGS = 5;

const TAG_BADGE_CLASS =
  "text-xs font-medium bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100 dark:bg-purple-950/40 dark:text-purple-300 dark:border-purple-800 dark:hover:bg-purple-900/40 transition-colors";

/**
 * Read-only header tags: show the first few inline, reveal the rest in a
 * Mantine popover (portals above the Mantine detail modal — no z-index tricks).
 * The "+N more" badge is a real expander, never a dead label.
 */
function ReadOnlyHeaderTags({ tags }: { tags: string[] }) {
  const [opened, setOpened] = React.useState(false);
  const visible = tags.slice(0, READONLY_VISIBLE_TAGS);
  const hidden = tags.slice(READONLY_VISIBLE_TAGS);

  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-sm font-medium text-gray-500 mr-2">Tags:</span>
      {visible.map((tag, index) => (
        <Badge key={index} variant="secondary" className={TAG_BADGE_CLASS}>
          {tag}
        </Badge>
      ))}
      {hidden.length > 0 && (
        <Popover
          position="bottom"
          withArrow
          shadow="md"
          opened={opened}
          onClose={() => setOpened(false)}
        >
          <Popover.Target>
            <Badge
              variant="outline"
              className="text-xs font-medium bg-card-50 text-gray-600 border-gray-200 cursor-pointer hover:bg-gray-100 dark:text-gray-400 dark:border-gray-700 dark:hover:bg-gray-800 transition-colors"
              onClick={() => setOpened((o) => !o)}
            >
              +{hidden.length} more
            </Badge>
          </Popover.Target>
          <Popover.Dropdown>
            <Stack gap="xs" style={{ maxWidth: 350 }}>
              <Text size="sm" fw={600}>
                Remaining Tags ({hidden.length})
              </Text>
              <ScrollArea.Autosize mah={300}>
                <Group gap="xs">
                  {hidden.map((tag) => (
                    <Badge key={tag} variant="secondary" className={TAG_BADGE_CLASS}>
                      {tag}
                    </Badge>
                  ))}
                </Group>
              </ScrollArea.Autosize>
            </Stack>
          </Popover.Dropdown>
        </Popover>
      )}
    </div>
  );
}

/**
 * The header entity-data layer carries tags as `unknown[]` (raw `item.tags`),
 * but both the inline editor and the read-only badges treat them as strings —
 * matching the existing `String(tag)` render. Normalize at this boundary so the
 * `string[]` consumers below stay type-correct without an `any`/`as` workaround.
 */
function toStringTags(tags: readonly unknown[]): string[] {
  return tags.map((tag) => String(tag));
}

export const HeaderTags = <T extends BaseEntity>({
  entityData,
  itemAsRecord,
  config,
  onTagsUpdate,
}: {
  entityData: DetailHeaderEntityData;
  itemAsRecord: Record<string, unknown>;
  config: SimpleTableConfig<T>;
  onTagsUpdate?: (newTags: string[]) => Promise<void>;
}) => {
  if (!entityData.tags || (entityData.tags.length === 0 && !onTagsUpdate)) return null;

  const tags = toStringTags(entityData.tags);

  return (
    <div>
      {onTagsUpdate ? (
        <InlineTagsEditor
          tags={tags}
          entityId={itemAsRecord.id as string}
          entityType={config.entity as "contacts" | "companies" | "deals" | "tickets" | "tasks"}
          onTagsUpdate={onTagsUpdate}
          maxTags={20}
          maxDisplayed={10}
          expandInline={config.entity === "tickets"}
          globalModalId={`tags-editor-${config.entity}-${itemAsRecord.id}`}
        />
      ) : (
        <ReadOnlyHeaderTags tags={tags} />
      )}
    </div>
  );
};
